import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import {
  auditMachines,
  auditRun,
  auditScoped,
  auditStatus,
  auditStop,
  type AuditHandOut,
  type AuditQueueAnswer,
  type AuditScopeFields,
  type AuditStep,
  type AuditTypePage,
} from '../../../api/audit'
import { ApiError } from '../../../api/client'
import { toast } from '../../../ui/notify'

/**
 * Rularea unui audit: tinta, adancimea, butonul, si urmarirea in timp real -
 * blocul `#auditScope` din `settings/audit/type.php` si secventiatorul din
 * `public/js/settings/audit.js` (TYPE PAGE: RUN, WATCH, ASK).
 *
 * -----------------------------------------------------------------------------
 * SECVENTIATORUL
 * -----------------------------------------------------------------------------
 *
 * Un audit e o coada de intrebari - una pe arie, apoi rezumatul. La fiecare
 * cincisprezece secunde pagina intreaba serverul cum sta coada; serverul
 * inchide pasul al carui raspuns a venit si da mai departe URMATOAREA
 * intrebare, deja marcata "running". In portal, intrebarea se tasta in
 * sertarul lui Adam de pe pagina; in v4 sertarul nu e inca aici, deci
 * intrebarea data mai departe se ARATA, cu cine o duce mai departe scris
 * langa ea - nimic nu se preface ca raspunde.
 *
 * O rulare incheiata e o fotografie - o foaie a ei; se merge acolo, nu se
 * reincarca planul, care nu mai arata niciun raspuns.
 */

const WATCH_EVERY_MS = 15000

interface Chosen {
  id: number
  name: string
}

export interface RunWatch {
  runId: number
  steps: AuditStep[]
  next: AuditHandOut | null
  status: string
  conversationId: number
}

export function stepStatesOf(watch: RunWatch | null): Record<string, string> {
  const out: Record<string, string> = {}
  for (const step of watch?.steps ?? []) out[step.area] = step.status
  return out
}

/** Bifele din panou, in campurile pe care le citeste `Target::fromPost`. */
function scopeFields(root: HTMLElement | null, depth: string, chosen: Chosen[]): AuditScopeFields {
  const pick = (name: string) =>
    root
      ? Array.from(root.querySelectorAll<HTMLInputElement>(`input[name="${name}"]:checked`))
          .map((i) => i.value)
          .join(',')
      : ''
  return {
    depth,
    devices: chosen.map((m) => m.id).join(','),
    sides: pick('sides'),
    classes: pick('classes'),
    offices: pick('offices'),
    types: pick('types'),
  }
}

export function useRunWatch(initial: RunWatch | null) {
  const [watch, setWatch] = useState<RunWatch | null>(initial)
  const queryClient = useQueryClient()

  useEffect(() => {
    if (!watch || watch.status === 'done' || watch.status === 'failed' || watch.status === 'stopped') return
    const runId = watch.runId
    /*
     * PANDA CARE NU TIPA LA NESFARSIT (audit.js `watch`, cec-my-26.9.13.x).
     * Dupa trei caderi la rand se opreste si spune O DATA ce a raspuns
     * serverul; cand serverul raspunde dar nu poate citi rularea (`why`), la
     * fel - la cincisprezece secunde nu se dreage. Rularea nu se pierde: ea e
     * a serverului; pandirea e doar fereastra prin care ne uitam la ea.
     */
    let missed = 0
    const timer = window.setInterval(() => {
      if (document.hidden) return
      auditStatus(runId)
        .then((r) => {
          missed = 0
          if (!r.ok) {
            if (r.why) {
              window.clearInterval(timer)
              toast(String(r.why), 'IT Audit', 'error')
            }
            return
          }
          const status = r.status ?? ''
          setWatch({ runId, steps: r.steps ?? [], next: r.next ?? null, status, conversationId: r.conversation_id ?? 0 })
          if (status === 'done' || status === 'failed' || status === 'stopped') {
            window.clearInterval(timer)
            void queryClient.invalidateQueries({ queryKey: ['settings', 'audit'] })
            window.location.assign(`/app/settings/audit/snapshot/${runId}`)
          }
        })
        .catch((e: unknown) => {
          /* Anulari si iesiri din sesiune: nu sunt caderi ale pandirii. */
          const code = e instanceof ApiError ? e.status : 0
          if (code === 0 || code === 401 || code === 403) return
          missed++
          if (missed < 3) return
          window.clearInterval(timer)
          const body = e instanceof Error ? e.message.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim() : ''
          toast(
            `The audit keeps running in the drawer, but this page stopped following it: HTTP ${code}${body ? ' - ' + body.substring(0, 200) : ''}`,
            'IT Audit',
            'error',
          )
        })
    }, WATCH_EVERY_MS)
    return () => window.clearInterval(timer)
  }, [watch?.runId, watch?.status, queryClient]) // eslint-disable-line react-hooks/exhaustive-deps

  return [watch, setWatch] as const
}

/** Butoanele din capul paginii. */
export function RunActions({
  page,
  watch,
  onStart,
  busy,
}: {
  page: AuditTypePage
  watch: RunWatch | null
  onStart: () => void
  busy: boolean
}) {
  if (!page.mayRun) {
    return <span className="adm-muted">Read-only: running an audit needs the analyst&apos;s right.</span>
  }
  if (!page.ready) {
    return (
      <span className="adm-muted od-audit-not-ready">
        Re-apply schema first <span>Change Log &rsaquo; Re-apply schema creates the audit tables</span>
      </span>
    )
  }
  const running = watch !== null && watch.status === 'running'
  if (running) {
    /* Oprirea: capitolele raspunse raman, cele care urmau nu se mai intreaba
       - ca `.od-audit-stop` din portal. Rularea oprita e o fotografie. */
    const onStop = () => {
      if (!window.confirm('Stop this audit?\n\nThe chapters already answered are kept. '
        + 'The ones still waiting will not be asked - to get them you would start a new run.')) return
      auditStop(watch.runId)
        .then((r) => {
          if (r.ok) window.location.assign(`/app/settings/audit/snapshot/${watch.runId}`)
          else window.alert(r.error || 'It could not be stopped.')
        })
        .catch(() => window.alert('It could not be stopped.'))
    }
    return (
      <span className="adm-muted">
        <i className="fas fa-circle-notch fa-spin" aria-hidden="true"></i> Running - the queue is watched every 15 seconds.{' '}
        <button type="button" className="btn btn-default od-audit-stop" data-run={watch.runId} onClick={onStop}>
          <i className="fas fa-stop" aria-hidden="true"></i> Stop this run
        </button>
      </span>
    )
  }
  return (
    <>
      <a className="btn btn-default" href={`/app/settings/audit/ask/${page.type.id}`}>
        <i className="fas fa-comments" aria-hidden="true"></i> Ask the client
      </a>
      <button type="button" className="btn btn-primary" id="auditStart" disabled={busy} onClick={onStart}>
        <i className="fas fa-play" aria-hidden="true"></i> Run audit
      </button>
    </>
  )
}

/**
 * CE anume se auditeaza sta langa NUMELE auditului.
 *
 * Statea in dreapta, dupa cifre, intre „Ask the client" si „Run audit" - adica
 * un control care schimba INTELESUL a tot ce scrie mai jos, citit ultimul.
 * Tinta apartine titlului: „IT Infrastructure & Architecture Audit, pe tot
 * parcul" e o singura propozitie.
 */
export function ScopeButton({
  page,
  open,
  says,
  onToggle,
}: {
  page: AuditTypePage
  open: boolean
  says: string
  onToggle: () => void
}) {
  if (!page.mayRun || !page.ready) return null

  return (
    <button
      type="button"
      className="btn btn-default btn-sm"
      id="auditScopeOpen"
      aria-expanded={open}
      aria-controls="auditScope"
      onClick={onToggle}
    >
      <i className="fas fa-crosshairs" aria-hidden="true"></i> <span id="auditScopeSays">{says}</span>
    </button>
  )
}

/** Panoul de tinta, impaturit: implicit e "tot, standard" si nimeni nu trebuie sa-l atinga ca sa porneasca. */
export function ScopePanel({
  page,
  open,
  depth,
  onDepth,
  chosen,
  onChosen,
  rootRef,
  count,
  onChange,
  onClear,
}: {
  page: AuditTypePage
  open: boolean
  depth: string
  onDepth: (d: string) => void
  chosen: Chosen[]
  onChosen: (c: Chosen[]) => void
  rootRef: React.RefObject<HTMLDivElement | null>
  count: string
  onChange: () => void
  onClear: () => void
}) {
  const [found, setFound] = useState<Chosen[]>([])
  const [typed, setTyped] = useState('')
  const findTimer = useRef<number | null>(null)

  function onType(v: string) {
    setTyped(v)
    if (findTimer.current) window.clearTimeout(findTimer.current)
    if (v.trim().length < 2) {
      setFound([])
      return
    }
    findTimer.current = window.setTimeout(() => {
      auditMachines(v.trim())
        .then((r) => setFound(r.ok ? r.machines.map((m) => ({ id: m.ID, name: m.name })) : []))
        .catch(() => setFound([]))
    }, 220)
  }

  return (
    <div className="adm-card od-audit-scope" id="auditScope" hidden={!open} ref={rootRef} onChange={onChange}>
      <div className="od-audit-scope-grid">
        <div className="od-audit-scope-col">
          <span className="od-audit-step-label">
            <i className="fas fa-layer-group" aria-hidden="true"></i> Which half
          </span>
          <label className="od-audit-pick">
            <input type="checkbox" name="sides" value="central" defaultChecked={page.before.sides.includes('central')} /> Centralised
          </label>
          <label className="od-audit-pick">
            <input type="checkbox" name="sides" value="endpoint" defaultChecked={page.before.sides.includes('endpoint')} /> Endpoints
          </label>
          <span className="adm-muted">Neither ticked means both.</span>
        </div>
        <div className="od-audit-scope-col">
          <span className="od-audit-step-label">
            <i className="fas fa-server" aria-hidden="true"></i> Kind of machine
          </span>
          <label className="od-audit-pick">
            <input type="checkbox" name="classes" value="servers" defaultChecked={page.before.classes.includes('servers')} /> Servers &amp; storage
          </label>
          <label className="od-audit-pick">
            <input type="checkbox" name="classes" value="network" defaultChecked={page.before.classes.includes('network')} /> Network gear
          </label>
          <label className="od-audit-pick">
            <input type="checkbox" name="classes" value="workstations" defaultChecked={page.before.classes.includes('workstations')} /> Workstations
          </label>
        </div>
        <div className="od-audit-scope-col">
          <span className="od-audit-step-label">
            <i className="fas fa-building" aria-hidden="true"></i> Sites
          </span>
          {page.offices.length === 0 && <span className="adm-muted">No sites recorded for this company.</span>}
          <div className="od-audit-scroll">
            {page.offices.map((office) => (
              <label className="od-audit-pick" key={office.ID}>
                <input type="checkbox" name="offices" value={office.ID} defaultChecked={page.before.offices.includes(String(office.ID))} /> {office.name} <span className="adm-muted">{office.n}</span>
              </label>
            ))}
          </div>
        </div>
        <div className="od-audit-scope-col">
          <span className="od-audit-step-label">
            <i className="fas fa-tags" aria-hidden="true"></i> Device types
          </span>
          <div className="od-audit-scroll">
            {page.deviceTypes.map((one) => (
              <label className="od-audit-pick" key={one.ID}>
                <input type="checkbox" name="types" value={one.ID} defaultChecked={page.before.types.includes(String(one.ID))} /> {one.name} <span className="adm-muted">{one.n}</span>
              </label>
            ))}
          </div>
        </div>
        <div className="od-audit-scope-col od-audit-scope-wide">
          <span className="od-audit-step-label">
            <i className="fas fa-desktop" aria-hidden="true"></i> Or name the machines
          </span>
          <input
            type="text"
            className="form-control input-sm"
            id="auditDevices"
            placeholder="type a name and pick from the list"
            value={typed}
            onChange={(e) => onType(e.target.value)}
          />
          {found.length > 0 && (
            <div className="od-audit-found" id="auditFound">
              {found.map((m) => (
                <button
                  type="button"
                  key={m.id}
                  onClick={() => {
                    if (!chosen.some((x) => x.id === m.id)) onChosen([...chosen, m])
                    setFound([])
                    setTyped('')
                  }}
                >
                  {m.name}
                </button>
              ))}
            </div>
          )}
          <div className="od-audit-chosen" id="auditChosen">
            {chosen.map((m) => (
              <span key={m.id} className="adm-pill">
                {m.name}{' '}
                <button type="button" aria-label={`drop ${m.name}`} onClick={() => onChosen(chosen.filter((x) => x.id !== m.id))}>
                  &times;
                </button>
              </span>
            ))}
          </div>
          <span className="adm-muted">Naming machines silences every filter above: somebody who named three servers named them on purpose.</span>
        </div>
        <div className="od-audit-scope-col od-audit-scope-wide">
          <span className="od-audit-step-label">
            <i className="fas fa-tachometer-alt" aria-hidden="true"></i> How deep
          </span>
          <div className="od-audit-depths">
            {Object.entries(page.depths).map(([key, one]) => (
              <button type="button" key={key} className={`od-audit-depth${key === depth ? ' is-on' : ''}`} data-depth={key} onClick={() => onDepth(key)}>
                <strong>{one.label}</strong>
                <span>{one.about}</span>
                <em>{one.cost}</em>
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="od-audit-scope-foot">
        <span id="auditScopeCount" className="adm-muted">
          {count}
        </span>
        <button type="button" className="btn btn-link btn-sm" id="auditScopeClear" onClick={onClear}>
          Reset to the whole estate
        </button>
      </div>
    </div>
  )
}

/**
 * Intrebarea data mai departe, cat timp sertarul lui Adam nu e pe pagina:
 * se arata, ca omul sa stie unde e coada, nu se preface ca s-a trimis.
 */
export function NextHandOut({ watch }: { watch: RunWatch | null }) {
  if (!watch || !watch.next) return null
  return (
    <div className="adm-card od-audit-handout">
      <span className="od-audit-step-label">
        <i className="fas fa-comment-dots" aria-hidden="true"></i> Handed out to Adam - {watch.next.title}
      </span>
      <p className="adm-muted">
        The agent&apos;s drawer is not on this page yet; the question waits in conversation #{watch.conversationId} and the queue is
        checked every 15 seconds.
      </p>
    </div>
  )
}

/** Tot ce tine de o pornire: starea panoului, numaratoarea, apasarea. */
export function useRun(page: AuditTypePage) {
  /*
   * Se porneste de la tinta ULTIMEI rulari, nu de la „tot, standard".
   *
   * Serverul o trimite in `before` si bifeaza casutele prin `defaultChecked`;
   * aici se reiau cele doua lucruri pe care casutele nu le pot purta - masinile
   * numite si adancimea - si se cere odata numarul, ca eticheta de langa titlu
   * sa spuna adevarul inainte sa fie deschis panoul. Cine auditeaza trimestrial
   * acelasi sediu il gaseste ales; cine vrea altceva schimba.
   */
  const [scopeOpen, setScopeOpen] = useState(false)
  const [depth, setDepth] = useState(page.before.depth || 'standard')
  const [chosen, setChosen] = useState<Chosen[]>(
    page.before.named.map((m) => ({ id: m.id, name: m.name })),
  )
  const [count, setCount] = useState('Counting…')
  const [says, setSays] = useState('Whole estate · standard')
  const [busy, setBusy] = useState(false)
  const rootRef = useRef<HTMLDivElement | null>(null)
  const countTimer = useRef<number | null>(null)
  const initial: RunWatch | null =
    page.latest && page.latest.status === 'running'
      ? { runId: page.latest.ID, steps: [], next: null, status: 'running', conversationId: page.latest.conversation_id }
      : null
  const [watch, setWatch] = useRunWatch(initial)

  const fields = () => scopeFields(rootRef.current, depth, chosen)

  function recount() {
    if (countTimer.current) window.clearTimeout(countTimer.current)
    countTimer.current = window.setTimeout(() => {
      auditScoped(fields())
        .then((r) => {
          if (!r.ok) return
          const bits = Object.entries(r.sides).map(([side, n]) => `${n} ${side}`)
          setCount(`${r.total} machine${r.total === 1 ? '' : 's'}${bits.length ? ' (' + bits.join(', ') + ')' : ''}`)
          const label = page.depths[depth]?.label ?? depth
          setSays(`${r.label} · ${label.toLowerCase()}`)
        })
        .catch(() => setCount('The count could not be read.'))
    }, 250)
  }

  useEffect(() => {
    if (scopeOpen) recount()
  }, [scopeOpen, depth, chosen]) // eslint-disable-line react-hooks/exhaustive-deps

  /* Odata, la deschiderea paginii: fara asta eticheta ar spune „whole estate"
     peste niste casute bifate pe un singur sediu - exact minciuna pe care
     panoul o repara. */
  useEffect(() => {
    if (page.mayRun && page.ready) recount()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  function clear() {
    rootRef.current?.querySelectorAll<HTMLInputElement>('input[type="checkbox"]').forEach((i) => {
      i.checked = false
    })
    setChosen([])
    setDepth('standard')
    setSays('Whole estate · standard')
    recount()
  }

  function started(r: AuditQueueAnswer) {
    if (!r.ok) {
      toast(r.error ?? 'The run could not be started.', 'IT Audit', 'error')
      if (r.run_id) setWatch({ runId: r.run_id, steps: [], next: null, status: 'running', conversationId: r.conversation_id ?? 0 })
      return
    }
    toast('Adam has the brief - the queue is watched from here.', 'IT Audit')
    setWatch({ runId: r.run_id ?? 0, steps: r.steps ?? [], next: r.next ?? null, status: 'running', conversationId: r.conversation_id ?? 0 })
  }

  function start(area: string) {
    setBusy(true)
    auditRun(page.type.id, area, fields())
      .then(started)
      .catch(() => toast('The run could not be started.', 'IT Audit', 'error'))
      .finally(() => setBusy(false))
  }

  return { scopeOpen, setScopeOpen, depth, setDepth, chosen, setChosen, count, says, busy, rootRef, recount, clear, start, watch }
}
